import {BotData} from '../../../api/types';
import {MessagesQty} from '../../../common-lib/src/components/BotTile/MessagesQty';
import styles from './TileV2.module.css';
import cn from 'classnames';
import {Link, useNavigate} from 'react-router-dom';
import stylesSkeleton from '../../styles/skeleton.module.css';
import {memo, useState, useRef} from 'react';

type Props = {
	bot: BotData;
	className?: string;
	onClick?: () => void;
};

const TileV2Inner = ({bot, className, onClick}: Props) => {
	const navigate = useNavigate();
	const [isImageLoaded, setIsImageLoaded] = useState(false);
	const [loadAttempts, setLoadAttempts] = useState(0);
	const imageRef = useRef<HTMLImageElement>(null);

	const handleTileClick = (e: React.MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();
		onClick?.();
		navigate(`/bot_${bot.id}/chat`);
	};

	const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
		const imageUrl = bot.attributes.heroPicture || bot.attributes.avatarUrl;
		const target = e.target as HTMLImageElement;

		if (loadAttempts < 1) {
			setLoadAttempts(prev => prev + 1);
			if (imageRef.current) {
				imageRef.current.src = '';
				setTimeout(() => {
					if (imageRef.current) {
						imageRef.current.src = imageUrl;
					}
				}, 100);
			}
			return;
		}

		const errorData = {
			botId: bot.id,
			imageUrl,
			error: e.type,
			timestamp: new Date().toISOString(),
			userAgent: navigator.userAgent,
			botName: bot.attributes.name,
			loadAttempts,
			//@ts-ignore
			errorMessage: target.error?.message || 'Unknown error',
			//@ts-ignore
			errorCode: target.error?.code,
			//@ts-ignore
			naturalWidth: target.naturalWidth,
			//@ts-ignore
			naturalHeight: target.naturalHeight,
			//@ts-ignore
			currentSrc: target.currentSrc,
			//@ts-ignore
			complete: target.complete,
			//@ts-ignore
			networkState: target.networkState,
			//@ts-ignore
			readyState: target.readyState,
			browserInfo: {
				language: navigator.language,
				platform: navigator.platform,
				vendor: navigator.vendor,
				connection: (navigator as any).connection?.type,
				effectiveType: (navigator as any).connection?.effectiveType,
				downlink: (navigator as any).connection?.downlink,
				rtt: (navigator as any).connection?.rtt
			}
		};

		fetch('https://imageloaderror-otah6f7ifq-uc.a.run.app', {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({data: errorData})
		}).catch(console.error);
	};

	if (!bot) {
		return <div className={cn(styles.container, stylesSkeleton.skeleton)} />;
	}

	return (
		<Link
			to={`/bot_${bot.id}/chat`}
			className={cn(styles.container, className)}
			onClick={handleTileClick}
		>
			<div className={cn(styles.imageContainer, {[stylesSkeleton.skeleton]: !isImageLoaded})}>
				<img
					ref={imageRef}
					src={bot.attributes.heroPicture || bot.attributes.avatarUrl}
					alt={bot.attributes.name}
					className={cn(styles.image, {[styles.loaded]: isImageLoaded})}
					loading="lazy"
					onLoad={() => setIsImageLoaded(true)}
					onError={handleImageError}
				/>
			</div>

			<MessagesQty
				messagesQty={bot.attributes.messagesCount}
				className={cn('bot-tile__messages-qty', styles.qty)}
			/>

			<div className={styles.namePhotoRow}>
				<p className={styles.name}>{bot.attributes.name}</p>
			</div>
			{bot.attributes.bio && (
				<p className={styles.greeting}>
					{/* googleoff: index */}
					<span aria-hidden="true">{bot.attributes.bio}</span>
					{/* googleon: index */}
				</p>
			)}
			<div className={styles.replyBtn}>
				<p className={styles.replyBtnText}>Chat</p>
			</div>
			<div className={styles.overlay} />
		</Link>
	);
};

export const TileV2 = memo(TileV2Inner, (prev, next) => {
	return prev.bot.id === next.bot.id;
});
